<template>
  <div class="dashboard_page_container">
    <div style="max-width: 540px; margin: 0 auto; width: 100%;">
      <div class="page-title-row" style="justify-content: center; text-align: center;">
        <h2>Datos de Facturación</h2>
      </div>

      <div class="card">
        <form @submit.prevent="saveForm" style="display: flex; flex-direction: column; gap: 1.25rem;">
          <div>
            <label for="company" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              Nombre de empresa:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="text"
              id="company"
              name="company"
              required
              v-model="nameEntered"
              :disabled="!(profileData.user_data.company_name == null || profileData.user_data.company_name.trim() === '')">
          </div>

          <div>
            <label for="invoice_name" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              Razón social:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="text"
              id="invoice_name"
              name="invoice_name"
              required
              v-model="invoiceNameEntered"
              :disabled="!(profileData.user_data.invoice_name == null || profileData.user_data.invoice_name.trim() === '')">
          </div>

          <div>
            <label for="address" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              Dirección:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="text"
              id="address"
              name="address"
              required
              v-model="addressEntered"
              :disabled="!(profileData.user_data.address == null || profileData.user_data.address.trim() === '')">
          </div>

          <div>
            <label for="cif" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              CIF:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="text"
              id="cif"
              name="cif"
              required
              v-model="cifEntered"
              :disabled="!(profileData.user_data.cif == null || profileData.user_data.cif.trim() === '' || profileData.user_data.cif === 'B00000000')">
          </div>

          <div>
            <label for="city" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              Ciudad:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="text"
              id="city"
              name="city"
              required
              v-model="cityEntered"
              :disabled="!(profileData.user_data.city == null || profileData.user_data.city.trim() === '')">
          </div>

          <div>
            <label for="new_password" style="font-weight: 600; font-size: 13.5px; color: var(--text-color-dark); display: block; margin-bottom: 0.35rem;">
              Nueva contraseña:
            </label>
            <input 
              v-if="profileData?.user_data" 
              type="password"
              id="new_password"
              name="new_password"
              placeholder="Deja en blanco para no cambiar"
              v-model="newPasswordEntered">
          </div>

          <button type="submit" class="main_button-green" style="width: 100%; margin-top: 0.75rem; padding: 12px;">
            Guardar Cambios
          </button>
        </form>
      </div>

      <div style="text-align: center; font-size: 12.5px; color: var(--text-color-light); margin-top: 1.5rem; line-height: 1.6;">
        <p style="margin-bottom: 0.5rem;">
          En este formulario puedes indicarnos tus datos de facturación. Si deseas modificar algún dato ya almacenado, escríbenos a
          <a href="mailto:hola@clickandplan.es" style="color: var(--primary-color) !important; font-weight: 600;">hola@clickandplan.es</a>.
        </p>
        <p style="margin-bottom: 0;">
          Los campos que no estén completados no aparecerán en tu factura mensual.
        </p>
      </div>
    </div>
    <ErrorPopup :errorMessage="error" />
  </div>
</template>

<script setup>
import ErrorPopup from "@/components/common/ErrorPopup.vue";
import { ref, nextTick } from "vue";
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { refreshToken } from "@/services/authService";

const error = ref(null);
const loadingStore = useLoadingStore();
const navigationStore = useNavigationStore();
const router = useRouter();

const profileData = ref(null);
const nameEntered = ref("");
const invoiceNameEntered = ref("");
const addressEntered = ref("");
const cifEntered = ref("");
const cityEntered = ref("");
const newPasswordEntered = ref("");
const getProfileData = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchProfileData();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchProfileData();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      showError("Se ha producido un error obteniendo tus datos de perfil.");
    }

    const data = await response.json();

    if (data != null) {
      profileData.value = data;
      nameEntered.value = data.user_data.company_name ?? "";
      invoiceNameEntered.value = data.user_data.invoice_name ?? "";
      addressEntered.value = data.user_data.address;
      
      cifEntered.value = data.user_data.cif ?? "";
      cityEntered.value = data.user_data.city ?? "";
    }
  } catch (exception) {
      // do nothing
  } finally {
    loadingStore.setLoading(false);
  }
};

async function fetchProfileData() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/profile/get_billing_data", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id')
    }),
  });
}

const saveForm = async (event) => {
  event.preventDefault();

  if (newPasswordEntered.value.trim().length > 0 && newPasswordEntered.value.trim().length < 6) {
    showError("La contraseña no es válida");
    return
  }

  loadingStore.setLoading(true);

  try {
    let response = await saveBillingData();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        await saveForm(event);
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
      }
    } else if (!response.ok) {
      showError("Se ha producido un error guardando tus datos de perfil.");
    } else {
      await getProfileData();
    }
  } catch (exception) {
      showError("Se ha producido un error guardando tus datos de perfil.");
  } finally {
    loadingStore.setLoading(false);
  }
};

async function saveBillingData() {
  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/profile/set_billing_data", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      user_id: localStorage.getItem('user_id'),
      company_name: nameEntered.value,
      invoice_name: invoiceNameEntered.value,
      address: addressEntered.value,
      cif: cifEntered.value,
      city: cityEntered.value,
      password: newPasswordEntered.value
    }),
  });
}

navigationStore.setNavigationPosition(4);

const showError = async (message) => {
  error.value = null;
  await nextTick();
  error.value = message;
};

getProfileData();


</script>

<style scoped>
  form {
    max-width: 400px;
    margin-top: 1rem;
    margin: 0 auto;
  }

  form div {
    margin-bottom: 1rem;
  }

  label {
    display: block;
    margin-bottom: 0.3rem;
  }

  input {
    width: 100%;
    padding: 0.4rem;
    box-sizing: border-box;
  }

  input:disabled {
    background-color: #f0f0f0 !important;
    border-color: #ffffff !important;
  }

</style>
